import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Trash2, Plus, X, LogOut } from 'lucide-react';
import { getEvents, createEvent, deleteEvent } from '../api/api';
import { useToast } from '../hooks/use-toast';

const AdminDashboard = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    description: '',
    image: ''
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if admin is logged in
    const adminAuth = localStorage.getItem('vibe_admin_auth');
    if (!adminAuth) {
      navigate('/admin/login');
      return;
    }
    loadEvents();
  }, [navigate]);

  const loadEvents = async () => {
    try {
      const data = await getEvents();
      setEvents(data);
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error loading events",
        description: error.message,
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createEvent(formData);
      toast({
        title: "Event Created!",
        description: "The event has been added successfully."
      });
      setFormData({ title: '', date: '', time: '', description: '', image: '' });
      setShowForm(false);
      loadEvents();
    } catch (error) {
      toast({
        title: "Error creating event",
        description: error.response?.data?.detail || error.message,
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await deleteEvent(eventId);
      toast({
        title: "Event Deleted",
        description: "The event has been removed."
      });
      loadEvents();
    } catch (error) {
      toast({
        title: "Error deleting event",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('vibe_admin_auth');
    navigate('/admin/login');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-black mb-2">Vibe Cafe Admin</h1>
            <p className="text-gray-600">Manage Events</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/')} variant="outline">
              View Website
            </Button>
            <Button onClick={handleLogout} variant="outline" className="flex items-center gap-2">
              <LogOut size={18} />
              Logout
            </Button>
          </div>
        </div>

        {/* Add Event Button */}
        <div className="mb-6">
          <Button 
            onClick={() => setShowForm(!showForm)}
            className="vibe-button"
          >
            {showForm ? <X className="mr-2" size={20} /> : <Plus className="mr-2" size={20} />}
            {showForm ? 'Cancel' : 'Add New Event'}
          </Button>
        </div>

        {/* Add Event Form */}
        {showForm && (
          <Card className="mb-8 border-2 border-yellow-400">
            <CardHeader>
              <CardTitle>Add New Event</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    placeholder="Weekly Bible Study"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Time *</Label>
                    <Input
                      id="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      required
                      placeholder="2:00 PM or TBA"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={3}
                    placeholder="Join us every Tuesday for..."
                  />
                </div>
                <div>
                  <Label htmlFor="image">Image URL *</Label>
                  <Input
                    id="image"
                    value={formData.image}
                    onChange={(e) => setFormData({...formData, image: e.target.value})}
                    required
                    placeholder="https://images.unsplash.com/..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Tip: Use <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Unsplash</a> for free images
                  </p>
                </div>
                <Button type="submit" className="vibe-button w-full">
                  Create Event
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Events List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <Card key={event.id} className="border-2 hover:border-yellow-400 transition-all">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={event.image} 
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader>
                <CardTitle className="text-lg">{event.title}</CardTitle>
                <p className="text-sm text-gray-600">
                  {new Date(event.date).toLocaleDateString()} at {event.time}
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">{event.description}</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleDelete(event.id)}
                    variant="destructive"
                    size="sm"
                    className="flex-1"
                  >
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {events.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No events yet. Click "Add New Event" to create one!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
